import React, { useState, useEffect } from 'react';
import { getStorageInfo } from '../lib/fileStorage';
import { getSyncStatus, addSyncStatusListener, SyncStatus, initSync } from '../lib/sync';
import { FeedSettings } from './FeedSettings';
import { LocationSettings } from './LocationSettings';
import { DeviceTransfer } from './DeviceTransfer';
import { getNotificationPermission, requestNotificationPermission } from '../lib/notifications';
import { COLORS, SPACING } from '../styles/theme';

export function NetworkSettings({ onBack, onOpenLegal, onOpenReports, isAdminUser }: { onBack: () => void; onOpenLegal: () => void; onOpenReports: () => void; isAdminUser: boolean }) {
  const [showTransfer, setShowTransfer] = useState(false);
  const [notifPermission, setNotifPermission] = useState(() => getNotificationPermission());
  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [seedingEnabled, setSeedingEnabled] = useState(true);
  const [networkStrength, setNetworkStrength] = useState(5);
  const [maxConnections, setMaxConnections] = useState(10);
  const [relayUrls, setRelayUrls] = useState<string[]>(() => {
    const settings = JSON.parse(localStorage.getItem('authenticulture_network_settings') || '{}');
    return settings.relayUrls || [];
  });
  const [newRelayUrl, setNewRelayUrl] = useState('');
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(getSyncStatus());

  useEffect(() => {
    updateStorageInfo();
  }, []);

  useEffect(() => {
    return addSyncStatusListener(setSyncStatus);
  }, []);

  const updateStorageInfo = async () => {
    const info = await getStorageInfo();
    setStorageInfo(info);
  };

  const handleAddRelay = () => {
    const trimmed = newRelayUrl.trim();
    if (!trimmed || relayUrls.includes(trimmed)) return;
    setRelayUrls([...relayUrls, trimmed]);
    setNewRelayUrl('');
  };

  const handleRemoveRelay = (url: string) => {
    setRelayUrls(relayUrls.filter(u => u !== url));
  };

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotifPermission(result);
  };

  const handleSave = () => {
    localStorage.setItem('authenticulture_network_settings', JSON.stringify({
      seedingEnabled,
      networkStrength,
      maxConnections,
      relayUrls
    }));
    initSync(relayUrls);
  };

  const storagePercent = storageInfo 
    ? Math.round((storageInfo.usage / storageInfo.quota) * 100) 
    : 0;

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        fontSize: '9px',
        fontWeight: 700,
        borderBottom: `2px solid ${COLORS.highlight}`,
        userSelect: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>Settings</span>
        <button
          onClick={onBack}
          style={{
            backgroundColor: 'transparent',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 700
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg, padding: SPACING.lg }}>
        {/* Sync Status */}
        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Sync Status
          </h3>
          <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: syncStatus.connected ? '#00AA00' : COLORS.textMuted }}>
            {syncStatus.connected
              ? `Connected (${syncStatus.relayCount} relay${syncStatus.relayCount !== 1 ? 's' : ''})`
              : 'Disconnected'}
          </p>
          <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.textMuted }}>
            Last sync: {syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleTimeString() : 'Never'}
          </p>
          {syncStatus.rateLimited && (
            <p style={{ margin: 0, fontSize: '9px', color: COLORS.accent, fontWeight: 700 }}>
              Warning: rate limited by relay
            </p>
          )}
        </div>

        {/* Device Transfer */}
        <div style={{ border: `2px outset ${COLORS.highlight}`, padding: SPACING.lg, marginBottom: SPACING.lg, backgroundColor: COLORS.panel }}>
          <h3 style={{ margin: `0 0 ${SPACING.md} 0`, fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, fontStyle: 'italic', color: COLORS.accent }}>Account</h3>
          {showTransfer
            ? <DeviceTransfer onClose={() => setShowTransfer(false)} />
            : <button onClick={() => setShowTransfer(true)} style={{ background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.sm} ${SPACING.md}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px' }}>Link a new device</button>
          }
        </div>

        {/* Relay Servers */}
        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Relay Servers
          </h3>
          {relayUrls.length === 0 && (
            <p style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: SPACING.md }}>
              No relays configured.
            </p>
          )}
          {relayUrls.map(url => (
            <div key={url} style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center', marginBottom: SPACING.sm }}>
              <span style={{ flex: 1, fontSize: '9px', color: COLORS.text, wordBreak: 'break-all' }}>{url}</span>
              <button
                onClick={() => handleRemoveRelay(url)}
                style={{
                  backgroundColor: COLORS.accent,
                  color: '#fff',
                  border: `2px outset ${COLORS.highlight}`,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: '9px',
                  padding: `${SPACING.xs} ${SPACING.sm}`,
                  fontWeight: 700
                }}
              >
                x
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.md }}>
            <input
              type="text"
              placeholder="wss://your-relay:3001"
              value={newRelayUrl}
              onChange={(e) => setNewRelayUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRelay()}
              style={{
                flex: 1,
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '9px'
              }}
            />
            <button
              onClick={handleAddRelay}
              style={{
                padding: `${SPACING.sm} ${SPACING.lg}`,
                backgroundColor: COLORS.panel,
                color: COLORS.accent,
                border: `2px outset ${COLORS.highlight}`,
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '9px',
                fontWeight: 700,
                textDecoration: 'underline'
              }}
            >
              Add
            </button>
          </div>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Storage
          </h3>
          <p style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: SPACING.md }}>
            {(storageInfo?.usage / 1024 / 1024).toFixed(1)}MB / {(storageInfo?.quota / 1024 / 1024).toFixed(0)}MB ({storagePercent}%)
          </p>
          <div style={{
            width: '100%',
            height: '16px',
            backgroundColor: '#000',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${storagePercent}%`,
              height: '100%',
              backgroundColor: COLORS.accent
            }} />
          </div>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Seeding
          </h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.md, cursor: 'pointer', marginBottom: SPACING.md }}>
            <input
              type="checkbox"
              checked={seedingEnabled}
              onChange={(e) => setSeedingEnabled(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '10px', color: COLORS.text }}>Enable seeding</span>
          </label>
          <p style={{ fontSize: '9px', color: COLORS.textMuted, margin: 0 }}>
            Share your videos with the network
          </p>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Network Strength
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md, marginBottom: SPACING.md }}>
            <input
              type="range"
              min="1"
              max="10"
              value={networkStrength}
              onChange={(e) => setNetworkStrength(Number(e.target.value))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '10px', color: COLORS.text, minWidth: '20px' }}>{networkStrength}</span>
          </div>
          <p style={{ fontSize: '9px', color: COLORS.textMuted, margin: 0 }}>
            Higher = more bandwidth, better connectivity (Phase 3)
          </p>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Max Connections
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
            <input
              type="number"
              min="1"
              max="50"
              value={maxConnections}
              onChange={(e) => setMaxConnections(Number(e.target.value))}
              style={{
                width: '80px',
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: 'monospace',
                fontSize: '10px'
              }}
            />
            <span style={{ fontSize: '10px', color: COLORS.text }}>simultaneous</span>
          </div>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Notifications
          </h3>
          <p style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: SPACING.md }}>
            Status: {
              notifPermission === 'granted' ? 'enabled'
              : notifPermission === 'denied' ? 'blocked — re-enable in your browser site settings'
              : notifPermission === 'unsupported' ? 'not supported by this browser'
              : 'not enabled'
            }
          </p>
          {notifPermission === 'default' && (
            <button
              onClick={handleEnableNotifications}
              style={{
                padding: `${SPACING.sm} ${SPACING.lg}`,
                backgroundColor: COLORS.accent,
                color: '#fff',
                border: `2px outset ${COLORS.highlight}`,
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '9px',
                fontWeight: 700,
                textDecoration: 'underline',
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)',
                marginBottom: SPACING.md,
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.borderStyle = 'inset';
                e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.borderStyle = 'outset';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
              }}
            >
              Enable browser notifications
            </button>
          )}
          <p style={{ fontSize: '9px', color: COLORS.textMuted, margin: 0 }}>
            Get OS-level alerts for replies, likes, DMs, and moderation actions on your posts when this tab is in the background.
          </p>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          backgroundColor: COLORS.panel,
          marginBottom: SPACING.lg
        }}>
          <h3 style={{
            margin: 0,
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent,
            borderBottom: `1px solid ${COLORS.border}`
          }}>
            Location
          </h3>
          <LocationSettings />
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: 0,
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent,
            borderBottom: `1px solid ${COLORS.border}`
          }}>
            Feed &amp; Algorithm Settings
          </h3>
          <FeedSettings />
        </div>

        {isAdminUser && (
          <div style={{
            border: `2px outset ${COLORS.highlight}`,
            padding: SPACING.lg,
            marginBottom: SPACING.lg,
            backgroundColor: COLORS.panel
          }}>
            <h3 style={{
              margin: `0 0 ${SPACING.sm} 0`,
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 700,
              fontStyle: 'italic',
              color: COLORS.accent
            }}>
              Moderation
            </h3>
            <button
              onClick={onOpenReports}
              style={{
                background: 'none',
                border: `1px solid ${COLORS.border}`,
                color: COLORS.text,
                cursor: 'pointer',
                padding: `${SPACING.xs} ${SPACING.md}`,
                fontFamily: 'monospace',
                fontSize: '10px'
              }}
            >
              View Reports
            </button>
          </div>
        )}

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.sm} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Legal
          </h3>
          <button
            onClick={onOpenLegal}
            style={{
              background: 'none',
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text,
              cursor: 'pointer',
              padding: `${SPACING.xs} ${SPACING.md}`,
              fontFamily: 'monospace',
              fontSize: '10px'
            }}
          >
            Terms, Privacy &amp; Acceptable Use Policy
          </button>
        </div>
      </div>

      <div style={{
        padding: SPACING.lg,
        borderTop: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.panel,
        display: 'flex',
        gap: SPACING.sm
      }}>
        <button
          onClick={handleSave}
          style={{
            padding: `${SPACING.sm} ${SPACING.lg}`,
            backgroundColor: COLORS.accent,
            color: '#fff',
            border: `2px outset ${COLORS.highlight}`,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: '9px',
            fontWeight: 700,
            textDecoration: 'underline',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.borderStyle = 'inset';
            e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.borderStyle = 'outset';
            e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}
